import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { promptData } from '../../../data/data_prompts';
import './PromptSetEdit.css';

function PromptSetEdit() {
  const navigate = useNavigate();
  const { promptSetId } = useParams();
  const location = useLocation();
  
  // Check if we're on the create route by looking at the URL path
  const isCreating = promptSetId === 'create' || location.pathname.includes('/administration/prompts/create');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [currentPromptSetId, setCurrentPromptSetId] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showChangeModelModal, setShowChangeModelModal] = useState(false);
  const [sectionToChangeModel, setSectionToChangeModel] = useState(null);
  const [newLLMModel, setNewLLMModel] = useState('');
  const [changingModel, setChangingModel] = useState(false);
  
  // Prompt set details
  const [promptSet, setPromptSet] = useState({
    name: '',
    type: '',
    active: true
  });
  
  // Sections data
  const [sections, setSections] = useState([]);
  const [availableScripts, setAvailableScripts] = useState([]);
  const [availableLLMModels, setAvailableLLMModels] = useState([]);
  
  // Form states
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSection, setNewSection] = useState({
    prompt_script_id: '',
    type: '',
    llm_model: ''
  });

  const scriptTypes = [
    { value: 'wrap_up', label: 'Wrap Up' },
    { value: 'objectives', label: 'Objectives' },
    { value: 'transcript', label: 'Transcript' },
    { value: 'follow_up', label: 'Follow Up' },
    { value: 'questions_answer', label: 'Questions Answer' },
    { value: 'questions_extraction', label: 'Questions Extraction' },
    { value: 'next_steps', label: 'Next Steps' },
    { value: 'next_steps_review', label: 'Next Steps Review' },
    { value: 'sentiment', label: 'Sentiment' },

  ];

  useEffect(() => {
    
    if (isCreating) {
      // When creating, we only need to fetch available scripts
      fetchAvailableScripts();
    } else {
      // When editing, fetch both prompt set data and available scripts
      // Only if we have a valid numeric ID
      if (promptSetId && !isNaN(promptSetId)) {
        setCurrentPromptSetId(promptSetId);
        fetchPromptSetData();
      } else {
        // If we don't have a valid ID, just fetch available scripts
        fetchAvailableScripts();
      }
    }
  }, [promptSetId]);

  const fetchAvailableScripts = async () => {
    try {
      setLoading(true);
      const [scriptsResponse, modelsResponse] = await Promise.all([
        promptData.getPromptScripts(),
        promptData.getLLMModels('active')
      ]);
      
      if (scriptsResponse && scriptsResponse.success) {
        setAvailableScripts(scriptsResponse.promptScripts || []);
      }
      
      if (modelsResponse && modelsResponse.success) {
        const models = modelsResponse.llmModels || [];
        setAvailableLLMModels(models);
      }
    } catch (err) {
      console.error('Error fetching available scripts and models:', err);
      setError('Error loading available scripts and models');
    } finally {
      setLoading(false);
    }
  };

  const fetchPromptSetData = async (id = promptSetId) => {
    try {
      setLoading(true);
      const [promptSetResponse, scriptsResponse, modelsResponse] = await Promise.all([
        promptData.getPromptSet(id),
        promptData.getPromptScripts(),
        promptData.getLLMModels('active')
      ]);

      if (promptSetResponse && promptSetResponse.success) {
        setPromptSet(promptSetResponse.promptSet);
        setSections(promptSetResponse.sections || []);
      } else {
        setError('Failed to fetch prompt set data');
      }

      if (scriptsResponse && scriptsResponse.success) {
        setAvailableScripts(scriptsResponse.promptScripts || []);
      }
      
      if (modelsResponse && modelsResponse.success) {
        const models = modelsResponse.llmModels || [];
        setAvailableLLMModels(models);
      }
    } catch (err) {
      console.error('Error fetching prompt set data:', err);
      setError('Error loading prompt set data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (isCreating) {
        // Creating a new prompt set
        
        const response = await promptData.createPromptSet(promptSet.name, promptSet.type);
        
        if (response && response.success) {
          const newPromptSetId = response.promptSet?.id;
          
          if (newPromptSetId) {
            // Set the current prompt set ID for future operations
            setCurrentPromptSetId(newPromptSetId);
            // Update the URL to reflect the new ID
            window.history.replaceState(null, '', `/administration/prompts/edit/${newPromptSetId}`);
            // Fetch the full prompt set data
            await fetchPromptSetData(newPromptSetId);
            return;
          } else {
            setError('Failed to get prompt set ID from response');
          }
        } else {
          console.log('Create failed:', response);
          setError('Failed to create prompt set');
        }
      } else {
        // Updating an existing prompt set
        
        const response = await promptData.updatePromptSet(
          promptSetId, 
          promptSet.name, 
          promptSet.type, 
          promptSet.active
        );
        if (response && response.success) {
          return;
        } else {
          setError('Failed to update prompt set');
        }
      }
    } catch (err) {
      console.error('Error saving prompt set:', err);
      setError('Error saving prompt set');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSection = async () => {
    if (!newSection.prompt_script_id || !newSection.type || !newSection.llm_model) {
      setError('Please select a script, type, and LLM model');
      return;
    }

    // If we're creating a new prompt set, we need to save it first
    if (isCreating && !currentPromptSetId) {
      setError('Please save the prompt set details first before adding scripts');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const promptSetIdToUse = currentPromptSetId || promptSetId;
      
      console.log('Adding section with:', {
        promptSetId: promptSetIdToUse,
        prompt_script_id: newSection.prompt_script_id,
        type: newSection.type,
        llm_model: newSection.llm_model
      });

      const response = await promptData.createPromptSetSection(
        promptSetIdToUse,
        newSection.prompt_script_id,
        newSection.type,
        newSection.llm_model
      );

      console.log('Add section response:', response);

      if (response && response.success) {
        // Refresh sections data
        const promptSetResponse = await promptData.getPromptSet(promptSetIdToUse);
        if (promptSetResponse && promptSetResponse.success) {
          setSections(promptSetResponse.sections || []);
        }
        
        setNewSection({ prompt_script_id: '', type: '', llm_model: '' });
        setShowAddSection(false);
      } else {
        setError('Failed to add section');
      }
    } catch (err) {
      console.error('Error adding section:', err);
      setError('Error adding section');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSection = async (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    setSectionToDelete(section);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sectionToDelete) return;

    try {
      setDeleting(true);
      const response = await promptData.deletePromptSetSection(currentPromptSetId, sectionToDelete.id);
      
      if (response && response.success) {
        // Remove the deleted section from the list
        setSections(sections.filter(s => s.id !== sectionToDelete.id));
        setShowDeleteModal(false);
        setSectionToDelete(null);
      } else {
        setError('Failed to delete script section');
      }
    } catch (err) {
      console.error('Error deleting script section:', err);
      setError('Error deleting script section');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSectionToDelete(null);
  };

  const handleChangeModelClick = (section) => {
    setSectionToChangeModel(section);
    setNewLLMModel(section.llm_model || '');
    setShowChangeModelModal(true);
  };

  const handleChangeModelConfirm = async () => {
    if (!sectionToChangeModel || !newLLMModel) return;

    try {
      setChangingModel(true);
      const response = await promptData.updatePromptSetSection(
        currentPromptSetId,
        sectionToChangeModel.id,
        sectionToChangeModel.prompt_script_id,
        sectionToChangeModel.type,
        newLLMModel
      );
      
      if (response && response.success) {
        // Refresh sections data
        const promptSetResponse = await promptData.getPromptSet(currentPromptSetId);
        if (promptSetResponse && promptSetResponse.success) {
          setSections(promptSetResponse.sections || []);
        }
        setShowChangeModelModal(false);
        setSectionToChangeModel(null);
        setNewLLMModel('');
      } else {
        setError('Failed to update LLM model');
      }
    } catch (err) {
      console.error('Error updating LLM model:', err);
      setError('Error updating LLM model');
    } finally {
      setChangingModel(false);
    }
  };

  const handleChangeModelCancel = () => {
    setShowChangeModelModal(false);
    setSectionToChangeModel(null);
    setNewLLMModel('');
  };

  const handleValidation = async () => {
    const promptSetIdToUse = currentPromptSetId || promptSetId;
    
    if (!promptSetIdToUse) {
      setError('Please save the prompt set first before validating');
      return;
    }

    try {
      setValidating(true);
      setError(null);

      const response = await promptData.getPromptSetValidation(promptSetIdToUse);

      if (response && response.success) {
        setValidationResult(response);
        // Update the prompt set with validation info if provided
        if (response.promptSet) {
          setPromptSet(prev => ({ ...prev, ...response.promptSet }));
        }
      } else {
        setError('Failed to validate prompt set');
      }
    } catch (err) {
      console.error('Error validating prompt set:', err);
      setError('Error validating prompt set');
    } finally {
      setValidating(false);
    }
  };

  const getValidationStatusColor = (result) => {
    if (!result) return '';
    switch (result) {
      case 'valid':
      case 'partial-valid':
      case 'partial valid':
        return 'valid';
      case 'invalid':
        return 'invalid';
      default:
        return '';
    }
  };

  const getValidationStatusText = (result) => {
    if (!result) return '';
    switch (result) {
      case 'valid':
        return 'Valid';
      case 'partial-valid':
      case 'partial valid':
        return 'PARTIAL VALID';
      case 'invalid':
        return 'Invalid';
      default:
        return result;
    }
  };

  const getScriptName = (scriptId) => {
    const script = availableScripts.find(s => s.id === scriptId);
    return script ? script.name : 'Unknown Script';
  };

  const getTypeLabel = (type) => {
    const typeObj = scriptTypes.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  };

  const getModelName = (connectionId) => {
    if (!connectionId) return 'Default';
    const model = availableLLMModels.find(m => m.connection_id === connectionId);
    return model ? model.name : `Model ${connectionId}`;
  };

  if (loading) {
    return (
      <div className="prompt-set-edit-container">
        <div className="loading-state">
          <i className="ti ti-loader ti-spin"></i>
          <p>Loading prompt set...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="prompt-set-edit-container">
      <div className="edit-header">
        <button onClick={() => navigate('/administration/prompts')} className="back-button">
          <i className="ti ti-arrow-left"></i>
          Back to Prompt Sets
        </button>
        <div className="header-content">
          <h1>{isCreating ? 'Create New Prompt Set' : 'Edit Prompt Set'}</h1>
          {(!isCreating || currentPromptSetId) && (
            <div className="validation-section">
              <button 
                onClick={handleValidation} 
                disabled={validating}
                className="validate-button"
              >
                {validating ? (
                  <>
                    <i className="ti ti-loader ti-spin"></i>
                    Validating...
                  </>
                ) : (
                  <>
                    <i className="ti ti-check"></i>
                    Validate Prompt Set
                  </>
                )}
              </button>
              {validationResult && (
                <div className={`validation-result ${getValidationStatusColor(validationResult.validation?.result)}`}>
                  <i className={`ti ti-${getValidationStatusColor(validationResult.validation?.result) === 'valid' ? 'check' : 'alert-circle'}`}></i>
                  <span>{getValidationStatusText(validationResult.validation?.result)}</span>
                  {validationResult.validation?.status && (
                    <span className="validation-status">{validationResult.validation.status}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="ti ti-alert-circle"></i>
          {error}
        </div>
      )}

      <div className="edit-content">
        {/* Prompt Set Details Section */}
        <div className="details-section">
          <h2>Prompt Set Details</h2>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              value={promptSet.name}
              onChange={(e) => setPromptSet({ ...promptSet, name: e.target.value })}
              placeholder="Enter prompt set name"
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="type">Type</label>
            <select
              id="type"
              value={promptSet.type}
              onChange={(e) => setPromptSet({ ...promptSet, type: e.target.value })}
              className="form-input"
            >
              <option value="">Select a type</option>
              <option value="liminal">Liminal</option>
              <option value="template">Template</option>
            </select>
          </div>

          {(!isCreating || currentPromptSetId) && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={promptSet.active}
                  onChange={(e) => setPromptSet({ ...promptSet, active: e.target.checked })}
                  className="form-checkbox"
                />
                <span>Active</span>
              </label>
            </div>
          )}

          <button 
            onClick={handleSave} 
            disabled={saving || !promptSet.name || !promptSet.type}
            className="save-button"
          >
            {saving ? (
              <>
                <i className="ti ti-loader ti-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="ti ti-device-floppy"></i>
                Save Details
              </>
            )}
          </button>
        </div>

        {/* Scripts Section */}
        {(!isCreating || currentPromptSetId) && (
          <div className="scripts-section">
            <div className="section-header">
              <h2>Scripts</h2>
              <button 
                onClick={() => setShowAddSection(!showAddSection)} 
                className="add-button"
              >
                <i className="ti ti-plus"></i>
                Add Script
              </button>
            </div>

            {showAddSection && (
              <div className="add-section-form">
                <div className="form-group">
                  <label htmlFor="script-select">Script</label>
                  <select
                    id="script-select"
                    value={newSection.prompt_script_id}
                    onChange={(e) => setNewSection({ ...newSection, prompt_script_id: e.target.value })}
                    className="form-select"
                  >
                    <option value="">Select a script</option>
                    {availableScripts.map(script => (
                      <option key={script.id} value={script.id}>
                        {script.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="type-select">Type</label>
                  <select
                    id="type-select"
                    value={newSection.type}
                    onChange={(e) => setNewSection({ ...newSection, type: e.target.value })}
                    className="form-select"
                  >
                    <option value="">Select a type</option>
                    {scriptTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="llm-model-select">LLM Model</label>
                  <select
                    id="llm-model-select"
                    value={newSection.llm_model}
                    onChange={(e) => setNewSection({ ...newSection, llm_model: e.target.value })}
                    className="form-select"
                  >
                    <option value="">Select an LLM model</option>
                    {availableLLMModels.map(model => (
                      <option key={model.id} value={model.connection_id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-actions">
                  <button 
                    onClick={handleAddSection} 
                    disabled={saving || !newSection.prompt_script_id || !newSection.type || !newSection.llm_model}
                    className="add-section-button"
                  >
                    {saving ? (
                      <>
                        <i className="ti ti-loader ti-spin"></i>
                        Adding...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-plus"></i>
                        Add Section
                      </>
                    )}
                  </button>
                  <button 
                    onClick={() => {
                      setShowAddSection(false);
                      setNewSection({ prompt_script_id: '', type: '', llm_model: '' });
                    }} 
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="sections-list">
              {sections.length === 0 ? (
                <div className="empty-sections">
                  <i className="ti ti-message-circle"></i>
                  <p>No scripts added to this prompt set</p>
                </div>
              ) : (
                <div className="sections-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Script ID</th>
                        <th>Script</th>
                        <th>Type</th>
                        <th>LLM Model</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sections.map(section => (
                        <tr key={section.id}>
                          <td>{section.prompt_script_id}</td>
                          <td>{section.script_name}</td>
                          <td>
                            <span className="type-badge">{getTypeLabel(section.type)}</span>
                          </td>
                          <td>
                            <span className="llm-model-badge">
                              {getModelName(section.llm_model)}
                            </span>
                          </td>
                          <td>
                            <button 
                              onClick={() => handleChangeModelClick(section)}
                              className="change-model-button"
                              title="Change LLM Model"
                            >
                              <i className="ti ti-settings"></i>
                            </button>
                            <button 
                              onClick={() => handleRemoveSection(section.id)}
                              className="remove-button"
                              title="Remove Script"
                            >
                              <i className="ti ti-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="delete-confirmation-modal">
            <div className="modal-header">
              <h3>Delete Script Section</h3>
            </div>
            <div className="modal-content">
              <p>
                Are you sure you want to delete the script "<strong>{sectionToDelete?.script_name}</strong>" from this prompt set?
              </p>
              <p className="warning-text">
                This action cannot be undone. The script will be removed from this prompt set.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                onClick={handleDeleteCancel} 
                className="cancel-button"
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteConfirm} 
                className="delete-confirm-button"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <i className="ti ti-loader ti-spin"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="ti ti-trash"></i>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change LLM Model Modal */}
      {showChangeModelModal && (
        <div className="modal-overlay">
          <div className="change-model-modal">
            <div className="modal-header">
              <h3>Change LLM Model</h3>
            </div>
            <div className="modal-content">
              <p>
                Change the LLM model for script "<strong>{sectionToChangeModel?.script_name}</strong>"
              </p>
              <div className="form-group">
                <label htmlFor="new-llm-model">LLM Model</label>
                <select
                  id="new-llm-model"
                  value={newLLMModel}
                  onChange={(e) => setNewLLMModel(e.target.value)}
                  className="form-select"
                >
                  <option value="">Select an LLM model</option>
                  {availableLLMModels && availableLLMModels.length > 0 ? (
                    availableLLMModels.map(model => (
                      <option key={model.id} value={model.connection_id}>
                        {model.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No models available</option>
                  )}
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                onClick={handleChangeModelCancel} 
                className="cancel-button"
                disabled={changingModel}
              >
                Cancel
              </button>
              <button 
                onClick={handleChangeModelConfirm} 
                className="change-model-confirm-button"
                disabled={changingModel || !newLLMModel}
              >
                {changingModel ? (
                  <>
                    <i className="ti ti-loader ti-spin"></i>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="ti ti-settings"></i>
                    Update Model
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromptSetEdit; 